<div class="dem-static">
  <hr class="line" [ngStyle]="{ background: newColor }" />

  <!-- The shared header, with the same affordances a dynamic dashboard's has.
       Two of them are inputs (the Refresh button, and the Help icon's config);
       the brand label and the "Last refreshed" stamp are not — topnav reads
       those off FilterService, so this screen publishes them from
       publishTopnavContext() as soon as a brand resolves, which is also what
       triggers its brand-keyed kpi/get-last-refreshed-date call. -->
  <app-topnav
    [clientLogo]="clientLogo"
    [pagename]="dashboardTitle"
    [refreshTheDashboard]="true"
    [widgetHelpInfo]="topnavHelpInfo"
  ></app-topnav>

  <!-- The page below the topnav is a fixed-height column on desktop: the filter
       row is the first band and stays put, and .dem-scroll underneath is the only
       thing that scrolls. Under 992px both collapse back into a single scrolling
       box (see the responsive block in the stylesheet), because a filter bar that
       has wrapped onto two lines is too much of the screen to pin down. -->
  <div class="dem-body">
    <!-- ============================ FILTER BAR ============================ -->
    <div class="filter-bar">
      <span class="sf-label">Selected Filters</span>

      <span
        class="sf-nav"
        *ngIf="badgesOverflow"
        (click)="scrollBadges(-1)"
        title="Scroll left"
      >
        <svg width="16" height="16" viewBox="0 0 8 8" fill="none">
          <path
            d="M6.79059 7.11381C6.81139 7.06178 6.79965 7.00195 6.76179 6.96215L3.92262 3.99979L6.76151 1.03744C6.79965 0.997648 6.81111 0.93727 6.79058 0.885787C6.77005 0.833757 6.72125 0.799805 6.66739 0.799805L4.26742 0.799805C4.23196 0.799805 4.19808 0.814557 4.17302 0.840435L1.23972 3.9013C1.21386 3.92857 1.20078 3.96418 1.20078 3.9998C1.20078 4.03543 1.21384 4.07103 1.23972 4.09831L4.17302 7.15917C4.19808 7.18505 4.23196 7.1998 4.26742 7.1998L6.66739 7.1998C6.72125 7.19979 6.77005 7.16584 6.79059 7.11381Z"
            fill="#1363DF"
          />
        </svg>
      </span>

      <div class="badge-scroller" #badgeScroller>
        <!-- A chip appears once its filter has values, and not before: the
             strip used to name every filter from the first frame and hold a
             valueless one as a greyed name with a "–" where its count goes.
             That reads as a control, and it is not one — there is nothing behind
             it to open, and a filter whose API never answered would sit there
             for the life of the screen looking like something you could use.
             Each chip arrives as its own values do (Brand as soon as its call
             lands, the data-backed ones with the records), so the strip only
             ever offers what can actually be picked from.

             ng-container carries the *ngFor so the chip itself can carry the
             *ngIf — the two cannot share an element. -->
        <ng-container *ngFor="let f of filters">
          <div class="chip" *ngIf="hasOptions(f)">
            <div class="chip-toggle" (click)="toggleDropdown(f, $event)">
              <span class="chip-label mr-1">{{ f.label }}</span>
              <span
                class="chip-count"
                [style.background]="isPartial(f) ? '#FF9A3D' : '#1363df'"
                >{{ f.applied.size }}/{{ f.options.length }}</span
              >
              <span class="chip-caret">▾</span>
            </div>
          </div>
        </ng-container>

        <!-- The date badge takes the same "off default" amber the dimension chips'
             counts do, once the range has been moved off the loaded data period —
             the reference strip's findDateColor() rule, which returns #FF9A3D for a
             date that is no longer the default and nothing otherwise. Blue until
             then. dateChanged is the same flag the Reset chip is driven by, so the
             two can never disagree about whether the date is filtering. -->
        <div class="chip " (click)="openDateMenu($event)">
          <span
            class="chip-label date-chip"
            [class.date-chip-on]="dateChanged"
            >{{ dateRangeLabel }}</span
          >
          <span class="chip-caret">▾</span>
        </div>

        <!-- Cross-filter chips: the selections a widget's popup applied.
             The same shape as the dimension chips beside them, because they are
             the same kind of thing — a narrowing of the screen the user can see
             and undo. Two differences, both meaning something: each is tinted
             with the colour of the visual it came from, so a chip can be traced
             back to the card or band that made it; and it carries a ✕ rather
             than a caret, because there is no option list behind a clicked tile
             to drop down. -->
        <div
          class="chip sel-chip"
          *ngFor="let s of selections"
          [style.border-color]="s.color || null"
          [title]="'Filtered by ' + s.label"
        >
          <span class="sel-dot" [style.background]="s.color || null"></span>
          <span class="chip-label">{{ s.label }}</span>
          <span
            class="sel-x"
            title="Remove this filter"
            (click)="removeSelection(s, $event)"
            >✕</span
          >
        </div>
      </div>

      <span
        class="sf-nav"
        *ngIf="badgesOverflow"
        (click)="scrollBadges(1)"
        title="Scroll right"
      >
        <svg width="16" height="16" viewBox="0 0 8 8" fill="none">
          <path
            d="M1.20941 7.11381C1.18861 7.06178 1.20035 7.00195 1.23821 6.96215L4.07738 3.99979L1.23849 1.03744C1.20035 0.997648 1.18889 0.93727 1.20942 0.885787C1.22995 0.833757 1.27875 0.799805 1.33261 0.799805L3.73258 0.799805C3.76804 0.799805 3.80192 0.814557 3.82698 0.840435L6.76028 3.9013C6.78614 3.92857 6.79922 3.96418 6.79922 3.9998C6.79922 4.03543 6.78616 4.07103 6.76028 4.09831L3.82698 7.15917C3.80192 7.18505 3.76804 7.1998 3.73258 7.1998L1.33261 7.1998C1.27875 7.19979 1.22995 7.16584 1.20941 7.11381Z"
            fill="#1363DF"
          />
        </svg>
      </span>

      <!-- The reference strip's Reset chip, icon and all: the registered `x_mark`
           svg rather than a text ✕, so the two strips carry the same mark. -->
      <span
        class="chip reset-chip"
        *ngIf="anyPartial"
        (click)="resetFilters()"
        title="Reset all filters"
      >
        <mat-icon class="reset-icon" svgIcon="x_mark"></mat-icon>Reset
      </span>

      <!-- How the cross-filtering works, because a visual that only answers a
           MODIFIER click cannot advertise that on its own — nothing about a KPI
           figure says a bare click will be ignored. Retired the moment a filter is
           applied: a hint that stays after it has been acted on is just permanent
           chrome. -->
      <div
        class="xf-hint"
        *ngIf="!loading && !noData && !anyPartial && !pending"
        title="Hold Ctrl (or ⌘) and click the things you want — KPI figures, donut bands, tree rows, grid rows, dates — to build a selection. Every further Ctrl+click adds another; Ctrl+clicking something already picked takes it back out. The rest of the page previews the selection as you build it. Nothing is filtered until you press Apply: Clear or Esc discards the selection, and a plain click anywhere else — folding a branch, expanding a panel, opening a menu — leaves it exactly as it is."
      >
        Hold <span class="xf-key">Ctrl</span> and click to select
      </div>

      <!-- Per-view wording, and dropped entirely while there is nothing true to
           say — the History sentence needs a loaded range, so the box is absent
           rather than wrong until the first response lands. See noteText. -->
      <div class="note-box" *ngIf="noteText" [title]="noteText">
        {{ noteText }}
      </div>
    </div>

    <!-- Fixed dropdown for the open badge (kept outside the scroller so it is
         not clipped). -->
    <div class="filter-backdrop" *ngIf="openFilter" (click)="closeDropdown()"></div>
    <div
      class="badge-menu"
      *ngIf="openFilter as f"
      [style.left.px]="menuPos.left"
      [style.top.px]="menuPos.top"
      (click)="$event.stopPropagation()"
    >
      <ng-container
        *ngTemplateOutlet="filterControls; context: { f: f }"
      ></ng-container>
    </div>

    <!-- Date badge popup — reuses the shared app-calendar (same as filter-badges) -->
    <div
      class="filter-backdrop"
      *ngIf="dateMenuOpen"
      (click)="closeDateMenu()"
    ></div>
    <div
      class="date-menu"
      *ngIf="dateMenuOpen"
      [style.left.px]="datePos.left"
      [style.top.px]="datePos.top"
      (click)="$event.stopPropagation()"
    >
      <app-dual-calendar
        [DateConfig]="dateConfig"
        [selectedData]="dateSelectedData"
        (applyFilter)="onCalendarApply($event)"
      ></app-dual-calendar>
    </div>

    <!-- ====================== SELECTION POPUP (Qlik-style) ==================
         The card the widget in selection mode wears: how many values are picked,
         WHICH ones, which object the card is attached to, and the two ways out.
         Nothing is filtered until Apply — until then the picks only drive the
         preview (the picked items stay lit, everything else on the page dims).

         It names the values now, where it used to show a bare "3 selections".
         The count was the honest minimum and not much more: on a page that has
         just dimmed everything the picks exclude, the one question the popup has
         to answer is what Apply is about to commit, and a number cannot answer
         it. Each chip is also its own undo — the only way to drop ONE pick out of
         a set without ctrl+clicking back through a dimmed widget to find it.

         Rendered ONCE here at the root and placed against the owning widget's
         measured rect (positionToolbar), rather than sitting inside that widget:
         every widget on this screen is a %card and %card is overflow: hidden, so
         a card straddling a panel's top edge is clipped off at that edge. Fixed
         positioning is the same escape .cell-tip below uses, and for the same
         reason. Its height is a CONSTANT in positionToolbar(), which is why the
         chip row is capped (PENDING_CHIP_LIMIT) and is not allowed to wrap.

         stopPropagation: the document handler CONFIRMS an open selection, so a
         click landing on the card's own padding would otherwise apply the very
         set the user may be about to discard. -->
    <div
      class="sel-toolbar"
      *ngIf="pending"
      [style.left.px]="toolbarPos.left"
      [style.top.px]="toolbarPos.top"
      [class.sel-dragging]="toolbarDragging"
      (click)="$event.stopPropagation()"
      (mousedown)="onToolbarDragStart($event)"
    >
      <div class="sel-top">
        <span class="sel-count">{{ pendingCount }}</span>
        <span class="sel-text">selected</span>
        <!-- Which widget the selection belongs to. The outline says it too, but
             it is a 2px ring on a page where everything else has dimmed — see
             pendingWidgetLabel. -->
        <span
          class="sel-src"
          *ngIf="pendingWidgetLabel"
          [title]="pendingWidgetLabel"
          >{{ pendingWidgetLabel }}</span
        >
        <div class="sel-actions">
          <button
            type="button"
            class="sel-btn sel-cancel"
            title="Discard this selection (Esc)"
            (click)="cancelPending($event)"
          >
            Clear
          </button>
          <button
            type="button"
            class="sel-btn sel-confirm"
            title="Apply this selection as a filter"
            (click)="confirmPending($event)"
          >
            Apply
          </button>
        </div>
      </div>

      <!-- The picked values. A chip is a remove control end to end rather than
           only its ✕: the card is small and a 9px glyph should not be the only
           target on it. -->
      <div class="sel-picks">
        <span
          class="sel-pick"
          *ngFor="let s of pendingChips"
          [title]="'Remove ' + s.label + ' from this selection'"
          (click)="removePick(s, $event)"
        >
          <!-- The colour of the visual the pick came from, so a chip traces back
               to the band or card that made it — the same idea the committed
               chips in the filter strip carry. Dropped entirely for a selection
               with no colour, rather than drawn as a blank dot. -->
          <span
            class="sel-pick-dot"
            *ngIf="s.color"
            [style.background]="s.color"
          ></span>
          <span class="sel-pick-label">{{ s.label }}</span>
          <span class="sel-pick-x">✕</span>
        </span>
        <!-- What the row had no space for, named in its tooltip. Not a button:
             there is nothing to remove here that the chips do not already offer
             once the set is trimmed down to them. -->
        <span class="sel-pick-more" *ngIf="pendingExtra" [title]="pendingExtraTitle">
          +{{ pendingExtra }}
        </span>
      </div>
    </div>

    <!-- Shared Include / Exclude / Clear buttons (badge dropdown + panel
         ng-select).

         Multi-select only. A single-select filter (Brand) used to render this
         template with `single: true`, which showed the Include button on its own
         — pick a value, then press Include to mean it. There is nothing for that
         second step to decide: the chip holds exactly one value, so the pick IS
         the filter. It applies on the click now (toggleOption / onSingleModel),
         and the `single` flag this template carried went with the button. -->
    <ng-template #ieButtons let-f="f" let-sc="sc">
      <button
        class="btn include du-multi-select-btn"
        [class.disabled]="!canInclude(f)"
        [disabled]="!canInclude(f)"
        title="Include"
        (click)="$event.stopPropagation(); applyInclude(f, sc)"
      >
        <svg width="25" height="15" viewBox="0 0 15 15" fill="none">
          <rect x="7" width="8" height="3" fill="#1363DF" />
          <rect x="7" y="12" width="8" height="3" fill="#1363DF" />
          <path
            d="M6.15338 6.46002L-5.28031e-07 6.46003L-3.46196e-07 8.53998L6.15338 8.53998L3.76241 11.0228L5.18496 12.5L10 7.5L5.18496 2.5L3.76241 3.9772L6.15338 6.46002Z"
            fill="#1363DF"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.62947 4L9.5185 7H5V6.46003L6.15338 6.46002L5 5.26233V4H6.62947ZM8.01778 4H13V7H10.8663C10.8265 6.93118 10.7778 6.86608 10.7203 6.80634L8.01778 4Z"
            fill="#1363DF"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M9.5185 8L6.62947 11H6V8.69925L6.15338 8.53998H6V8H9.5185ZM10.8663 8H14V11H8.01778L10.7203 8.19366C10.7778 8.13392 10.8265 8.06882 10.8663 8Z"
            fill="#1363DF"
          />
        </svg>
      </button>

      <button
        class="btn exclude du-multi-select-btn"
        [class.disabled]="!canExclude(f)"
        [disabled]="!canExclude(f)"
        title="Exclude"
        (click)="$event.stopPropagation(); applyExclude(f, sc)"
      >
        <svg width="25" height="15" viewBox="0 0 15 15" fill="none">
          <rect width="8" height="3" fill="#D11044" />
          <rect y="12" width="8" height="3" fill="#D11044" />
          <path
            d="M11.1534 6.46002L5 6.46003L5 8.53998L11.1534 8.53998L8.76241 11.0228L10.185 12.5L15 7.5L10.185 2.5L8.76241 3.9772L11.1534 6.46002Z"
            fill="#D11044"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5 7H10V6.46003L5 6.46003V7ZM10 5.26234L8.78436 4H10V5.26234ZM4 7H2V4H7.76266C7.76816 4.24237 7.86131 4.48312 8.0421 4.67086L8.80207 5.46003L5 5.46003C4.44772 5.46003 4 5.90774 4 6.46003V7Z"
            fill="#D11044"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M8.78436 11H9V10.7761L8.78436 11ZM9 8.53998L5 8.53998V8H9V8.53998ZM7.76267 11H1V8H4V8.53998C4 9.09226 4.44772 9.53997 5 9.53998L8.80207 9.53998L8.0421 10.3291C7.86131 10.5169 7.76816 10.7576 7.76267 11Z"
            fill="#D11044"
          />
        </svg>
      </button>

      <!-- Clear selection means clear the FILTER, not the checkboxes: the chip goes
           back to every value and applies, which is what both references do (the
           badge dropdown's select-all-then-apply, the multi-select's 'clear'). Dead
           once everything is already picked, since that is not narrowing
           anything. -->
      <button
        class="btn exclude du-multi-select-btn"
        [class.disabled]="!canClear(f)"
        [disabled]="!canClear(f)"
        title="Clear selection"
        (click)="$event.stopPropagation(); applyClear(f, sc)"
      >
        <svg width="25" height="15" viewBox="0 0 16 16" fill="none">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M15.1233 0C15.6075 0 16 0.392499 16 0.876682C16 1.05902 15.9432 1.23684 15.8373 1.3854L12.2592 6.40765C11.9677 6.35237 11.6669 6.32344 11.3594 6.32344C8.70842 6.32344 6.55938 8.47248 6.55938 11.1234C6.55938 12.7503 7.3687 14.1881 8.60667 15.0562L7.97832 15.4751C7.19837 15.995 6.14461 15.7843 5.62468 15.0044C5.43971 14.7268 5.34054 14.401 5.33964 14.0675L5.32513 8.72486L0.159683 1.38108C-0.11886 0.985049 -0.0236423 0.438156 0.372391 0.159613C0.520046 0.055766 0.696221 0 0.876751 0H15.1233Z"
            fill="#D11044"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.2 14.3219C12.9673 14.3219 14.4 12.8892 14.4 11.1219C14.4 9.35456 12.9673 7.92188 11.2 7.92188C9.43269 7.92188 8 9.35456 8 11.1219C8 12.8892 9.43269 14.3219 11.2 14.3219ZM11.5953 11.4889H12.8V10.7642H11.5953L11.6 10.7643H10.8L10.7953 10.7642H9.60001V11.4889L10.7953 11.4889L11.5953 11.4889Z"
            fill="#D11044"
          />
        </svg>
      </button>
    </ng-template>

    <!-- Shared filter selection UI, reused by the badge dropdown above and the
         right-side filter panel below. -->
    <ng-template #filterControls let-f="f">
      <div class="filter-controls">
      <!-- Include / Exclude / Clear, and none of the three on a single-select
           badge (Brand). "Everything except" is not a state a filter that scopes
           the fetch can hold, and Include had nothing left to add once the value
           applies on the click — so the bar is absent rather than standing empty
           above the list. See #ieButtons. -->
      <div class="header" *ngIf="!f.single">
        <ng-container
          *ngTemplateOutlet="ieButtons; context: { f: f }"
        ></ng-container>
      </div>

      <div class="search-container">
        <input
          class="search-bar"
          type="text"
          placeholder="Search..."
          [(ngModel)]="f.search"
        />
      </div>

      <!-- Select All acts on the SEARCH RESULTS while a search is running (see
           filteredOptions / toggleSelectAll), which is why it renames itself — the
           same wording, and the same behaviour, as the reference badge dropdown. -->
      <div
        class="select-all-container"
        *ngIf="!f.single"
        (click)="toggleSelectAll(f, $event)"
      >
        <input
          type="checkbox"
          [checked]="allChecked(f)"
          (click)="toggleSelectAll(f, $event)"
        />
        <span *ngIf="!f.search">Select All</span>
        <span *ngIf="f.search">(Select All Search Results)</span>
      </div>

      <!-- Add current selection to filter: keep what the chip already filters by on
           top of what is ticked here, so a search + Include adds to the filter
           instead of replacing it. See FilterDef.addCurrent. -->
      <div
        class="select-all-container"
        *ngIf="!f.single"
        (click)="toggleAddCurrent(f, $event)"
      >
        <input
          type="checkbox"
          [checked]="f.addCurrent"
          (click)="toggleAddCurrent(f, $event)"
        />
        <span>Add current selection to filter</span>
      </div>

      <div
        class="menu-options"
        [class.dynamicHeight]="filteredOptions(f).length > 9"
      >
        <!-- A tick on a multi-select, a direct pick on a single-select: Brand
             applies and closes on this click rather than waiting for a button
             that is no longer there. See toggleOption(). -->
        <div
          class="du-badge-option"
          *ngFor="let o of filteredOptions(f)"
          [class.selected]="isChecked(f, o)"
          (click)="toggleOption(f, o, $event)"
        >
          <input
            type="checkbox"
            [checked]="isChecked(f, o)"
            (click)="toggleOption(f, o, $event)"
          />
          <span>{{ o }}</span>
        </div>
        <div class="no-data" *ngIf="filteredOptions(f).length === 0">
          No data found
        </div>
      </div>
      </div>
    </ng-template>

    <!-- Everything from here down is the scrolling region. The filter row above
         is deliberately outside it, so the scrollbar starts below the bar rather
         than running behind it.

         Deliberately NO (scroll)="hideCellTip()" here, however tempting: .cell-tip
         lives inside this element, and opening it inserts a panel that the browser
         answers with a scroll adjustment (scroll anchoring). That fires scroll,
         which retired the tooltip in the same breath as showing it — no tooltip,
         in either view. The strip's own .heat-scroll can carry the handler safely
         because the panel is a sibling of it rather than a child, which is exactly
         why it was put there. A page scroll can therefore still strand an open
         panel; mouseleave clears it on the way out. -->
    <div class="dem-scroll" (scroll)="onPageScroll()">
    <!-- ======================== KPI CARDS + DONUT ========================
         Every widget on this screen reads the one dataset, so they all wait on
         the same `loading` flag — but each card carries its own small loader
         rather than the row sharing one, so the cards stay in place and only the
         numbers are pending. The chrome (heading, icon, colour) is constant, so
         it is on screen from the first frame and nothing shifts when the values
         land. -->
    <div class="kpi-row" data-xf-widget="kpi" [class.xf-selecting]="isSelecting('kpi')">
      <!-- trackBy is what makes the figures animate at all, and it is not an
           optimisation bolted on afterwards: buildKpis() rebuilds `kpis` as fresh
           objects on EVERY filter change, so without a key ngFor destroys both
           cards and builds two new ones each time. That threw away the elements
           the count was running on (and re-created two mat-icon SVGs per card for
           a change that only moved a number), which is the flicker the row used to
           show on every click. Keyed by `key` — the same name buildKpis() looks
           each value up by, so the two cannot drift apart. -->
      <div class="kpi-card" *ngFor="let k of kpis; trackBy: trackKpi">
        <!-- Loading: the card is blank apart from its own loader — no heading,
             icon or watermark showing through behind it. The card keeps its
             96px so the row does not resettle when the numbers land. -->
        <app-progress-indefinite
          class="card-loader"
          *ngIf="loading"
        ></app-progress-indefinite>

        <ng-container *ngIf="!loading">
          <!-- Help, from assets/help_info/dem-static.json — the same affordance the
               real KPI cards carry (card2kpi): the icon both loads the text and
               opens the shared popover at the foot of this template. Absent on a
               card the JSON has no entry for, rather than opening an empty one. -->
          <div class="kpi-info" *ngIf="k.help">
            <mat-icon
              svgIcon="info_circle"
              matTooltip="Help"
              [matMenuTriggerFor]="cardHelpMenu"
              (click)="showCardHelp(k.help)"
            ></mat-icon>
          </div>

          <div class="kpi-body">
            <mat-icon
              class="kpi-icon"
              [svgIcon]="k.icon"
              [style.color]="k.color"
            ></mat-icon>

            <!-- Value over heading, which is the order the extract cards read in
                 (a big "2" with "Delayed" under it), where DPM's lead with the
                 heading. The sub-line only appears on a card that has one — the
                 In Process card's "Interim: 0"; Delayed has no second figure, and
                 an empty "…:" line would leave the two cards uneven.

                 Each figure is its own cross-filter target rather than the card
                 being one: the two lines count different things (In Process is a
                 status, its Interim sub-line a flag that cuts across the
                 statuses), so a click has to say which of them it meant. -->
            <div class="kpi-text">
              <div
                class="xf"
                [class.xf-lit]="isPicked(k.sel)"
                [class.xf-dim]="isDimmed(k.sel, 'kpi')"
                [title]="xfTitle(k.heading, k.sel)"
                (click)="onVisualClick(k.sel, $event, 'kpi')"
              >
                <!-- [duNumber] counts the figure across when a filter moves it —
                     see animated-number.directive.ts. The interpolation is gone
                     deliberately: the directive owns this element's text, and
                     `{{ k.value }}` beside it would stamp the new figure in one
                     frame before the count of it started.

                     [duNumberShort] abbreviates it the way the real KPI cards do
                     (card2kpi runs every figure through `numberFormat`): 12,450
                     reads as "12.45K", so a brand with a million files keeps its
                     count inside the card instead of pushing it into the
                     neighbour. Only the KPI cards abbreviate — the donut beside
                     them prints exact figures and fits them by other means. -->
                <div
                  class="kpi-value"
                  [style.color]="k.color"
                  [duNumberShort]="true"
                  [duNumber]="k.value"
                ></div>
                <div class="kpi-heading">{{ k.heading }}</div>
              </div>
              <div
                class="kpi-sub xf"
                *ngIf="k.subHeading"
                [class.xf-lit]="isPicked(k.subSel)"
                [class.xf-dim]="isDimmed(k.subSel, 'kpi')"
                [title]="xfTitle(k.subHeading, k.subSel)"
                (click)="onVisualClick(k.subSel, $event, 'kpi')"
              >
                <span>{{ k.subHeading }}:</span>
                <span
                  class="kpi-sub-val"
                  [duNumberShort]="true"
                  [duNumber]="k.subValue"
                ></span>
              </div>
            </div>
          </div>

          <mat-icon class="kpi-watermark" [svgIcon]="k.icon"></mat-icon>
        </ng-container>
      </div>

      <!-- Extracts: one card, three counts — the reference groups them because
           they answer one question (how much went out of how much was due), and
           none of the three is the card's headline. Same chrome as the cards
           above, different body. -->
      <div class="kpi-card extracts-card">
        <app-progress-indefinite
          class="card-loader"
          *ngIf="loading"
        ></app-progress-indefinite>

        <ng-container *ngIf="!loading">
          <div class="kpi-info" *ngIf="extractsHelp">
            <mat-icon
              svgIcon="info_circle"
              matTooltip="Help"
              [matMenuTriggerFor]="cardHelpMenu"
              (click)="showCardHelp(extractsHelp)"
            ></mat-icon>
          </div>

          <!-- Sent's own green, on the ring icon and nowhere else on this card.
               The card is about what went out, so its icon wears the colour "sent"
               carries everywhere else on this screen: the donut's arc and Sent
               swatch, and the Sent band on every heat tile.
               It stops at the icon deliberately. The three counts read as one
               figure in three parts, so they share the ink the other cards' figures
               use — greening only Sent made it the card's headline and left
               Expected, the number it is measured against, looking secondary. The
               watermark is left to its own neutral for the same reason: tinted
               green it read as a fourth, blurred copy of the icon. -->
          <div class="kpi-body">
            <mat-icon
              class="kpi-icon"
              svgIcon="download_circle"
              [style.color]="sentColor"
            ></mat-icon>

            <div class="extracts-inner">
              <div class="extracts-title">Extracts</div>
              <!-- Sent and Interim Sent cross-filter; Expected does not. It is
                   every row in the current view (aggregate()'s `expected` is
                   rows.length), so selecting it would narrow the screen to what
                   it already shows — it stays the denominator the other two are
                   read against. It still dims with everything else while a
                   highlight is live, rather than staying lit for a reason
                   nothing on screen explains. -->
              <div class="extracts-cols">
                <div
                  class="xf"
                  [class.xf-lit]="isPicked(extractsSel.sent)"
                  [class.xf-dim]="isDimmed(extractsSel.sent, 'kpi')"
                  [title]="xfTitle('Sent', extractsSel.sent)"
                  (click)="onVisualClick(extractsSel.sent, $event, 'kpi')"
                >
                  <div class="ex-label">Sent</div>
                  <!-- Counted across like the KPI cards' figures (see
                       animated-number.directive.ts). All three move together
                       under the one filter, and one ticker drives every figure
                       on the row off the same frame, so they stay in step
                       instead of each landing a frame apart. -->
                  <div
                    class="ex-value"
                    [duNumberShort]="true"
                    [duNumber]="extracts.sent"
                  ></div>
                </div>
                <div [class.xf-dim]="isDimmed(null, 'kpi')">
                  <div class="ex-label">Expected</div>
                  <div
                    class="ex-value"
                    [duNumberShort]="true"
                    [duNumber]="extracts.expected"
                  ></div>
                </div>
                <div
                  class="xf"
                  [class.xf-lit]="isPicked(extractsSel.interim)"
                  [class.xf-dim]="isDimmed(extractsSel.interim, 'kpi')"
                  [title]="xfTitle('Interim Sent', extractsSel.interim)"
                  (click)="onVisualClick(extractsSel.interim, $event, 'kpi')"
                >
                  <div class="ex-label">Interim Sent</div>
                  <div
                    class="ex-value"
                    [duNumberShort]="true"
                    [duNumber]="extracts.interim"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <mat-icon class="kpi-watermark" svgIcon="download_circle"></mat-icon>
        </ng-container>
      </div>

      <!-- Donut -->
      <div class="donut-card" data-xf-widget="donut" [class.xf-selecting]="isSelecting('donut')">
        <!-- Same treatment, and the same loader size: blank card, one loader. -->
        <app-progress-indefinite
          class="card-loader"
          *ngIf="loading"
        ></app-progress-indefinite>

        <div class="kpi-info" *ngIf="!loading && donutHelp">
          <mat-icon
            svgIcon="info_circle"
            matTooltip="Help"
            [matMenuTriggerFor]="cardHelpMenu"
            (click)="showCardHelp(donutHelp)"
          ></mat-icon>
        </div>

        <!-- data-fit is the card's dynamic type size: buildDonut() measures the
             longest line it is about to draw (a legend row, or the percentage
             and its ratio) and picks a tier, and the stylesheet scales the whole
             card's type by it — see .donut-inner[data-fit]. That is how this
             card copes with figures in the millions instead of abbreviating
             them: a ring is a card about proportions, and "24/28" shortened to
             two significant figures would stop being checkable against the
             percentage printed beside it. -->
        <div
          class="donut-inner"
          [attr.data-fit]="donutFit"
          *ngIf="!loading && donut"
        >
          <!-- The ring plots both series as concentric arcs, outermost first:
               Sent on the outside, Expected inside it. Each band gets its own
               grey track so a short arc still reads as a share of a whole rather
               than as a stray stroke, and its colour is the one that series wears
               in the legend beside it.

               Bands of their own, NOT one band with Expected as the track and
               Sent painted over it — that put two saturated colours in the same
               annulus and read as two arcs overlapping instead of one ring. See
               DonutArc in the component.

               Hovering a band names it and its value; the other band dims and the
               hovered one thickens, so it is clear which of the two the figure
               belongs to. -->
          <div class="donut-ring-wrap">
            <svg viewBox="0 0 90 90" class="donut-svg">
              <ng-container *ngFor="let arc of donut.arcs">
                <circle
                  class="donut-track"
                  cx="45"
                  cy="45"
                  [attr.r]="arc.r"
                ></circle>
                <!-- A band is a cross-filter source where it plots a share
                     (Sent); the band that IS the whole (Expected) carries no
                     `sel` and stays a plain arc — see DONUT_SEL. Both still
                     answer the hover tooltip either way. -->
                <circle
                  class="donut-progress"
                  [class.xf]="!!arc.sel"
                  [class.xf-lit]="isPicked(arc.sel)"
                  [class.xf-dim]="isDimmed(arc.sel, 'donut')"
                  cx="45"
                  cy="45"
                  [attr.r]="arc.r"
                  [style.stroke]="arc.color"
                  [attr.stroke-dasharray]="arc.dasharray"
                  [matTooltip]="arc.tooltip + xfSuffix(arc.sel)"
                  matTooltipClass="dem-donut-tip"
                  matTooltipPosition="above"
                  (click)="onVisualClick(arc.sel, $event, 'donut')"
                ></circle>
              </ng-container>
            </svg>
          </div>

          <div class="donut-meta">
            <div class="donut-pct-line">
              <span class="donut-pct">{{ donut.percentage }}</span>
              <span class="donut-ratio">{{ donut.ratio }}</span>
            </div>
            <div class="donut-title">Processing Status</div>
            <div class="donut-subtitle">(Sent vs Expected)</div>
          </div>

          <!-- The legend cross-filters too, and by the same selections the arcs
               and cards use — so clicking "In Process" here, on its KPI card, or
               on a heat tile's band all mean the one thing and light each other
               up. It reaches two series the ring itself does not draw (In Process
               and Delayed), which is the reason it is worth making clickable
               rather than leaving as a key to the arcs.

               Three of the four rows are sources; Expected is not — it is every
               row in view, so there is nothing to narrow to (see DONUT_SEL) — and
               it takes .xf only when there is a selection behind it. That class is
               what carries the pointer and the hover tint, and a row answering no
               click must wear neither: the same rule the Extracts card's Expected
               column and the ring's own inner band already follow. -->
          <div class="donut-legend">
            <div
              class="legend-item"
              *ngFor="let l of donut.legend"
              [class.xf]="!!legendSel(l.name)"
              [class.xf-lit]="isPicked(legendSel(l.name))"
              [class.xf-dim]="isDimmed(legendSel(l.name), 'donut')"
              [title]="xfTitle(l.name, legendSel(l.name))"
              (click)="onVisualClick(legendSel(l.name), $event, 'donut')"
            >
              <span class="legend-dot" [style.background]="l.color"></span>
              <span class="legend-name">{{ l.name }}</span
              >:
              <span class="legend-val">{{ l.value }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ========================= DATA RECEIPT STATUS =====================
         The panel hosts the shared <icon-chart> header, so Fullscreen /
         Expand / the export menu are the same components (and the same
         behaviour) as every dynamic dashboard widget. #heatFsEl is what
         icon-chart puts into fullscreen; #heatExportEl is the subtree it
         rasterises for "Export as Image". -->
    <div class="panel heat-panel" data-xf-widget="heat" [class.xf-selecting]="isSelecting('heat')">
      <article
        #heatFsEl
        fullscreen-able
        class="heat-fs"
        [class.is-active]="heatFullscreen"
      >
        <icon-chart
          [headerConfig]="heatHeaderConfig"
          [heading]="heatTitle"
          [chartName]="'DemStaticHeatmap'"
          [item]="heatItem"
          [staticInfo]="heatHelp"
          [data]="heatExcelRows"
          [excelConfig]="heatExcelConfig"
          [config]="heatExportEl"
          [expand]="heatFsEl"
          [fullscreen]="heatFullscreen"
          (screenChanges)="onHeatFullscreen()"
          (expendCol)="onHeatExpandToggle($event)"
        ></icon-chart>

        <!-- The states live inside #heatExportEl rather than beside it: the ref
             is an input to icon-chart above (the subtree it rasterises), and a
             ref declared inside an *ngIf is not visible to the parent view, so
             moving it would hand icon-chart undefined while loading. -->
        <div #heatExportEl class="heat-export">
          <!-- Loading / empty states. Both hold the panel at .panel-state's
               200px so the loading bar and the message have somewhere to sit and
               the page does not jump as the data lands; with data the panel goes
               back to sizing itself to the tree. The header above stays put
               either way, so Fullscreen / Expand / export are never missing. -->
          <div class="panel-state" *ngIf="loading">
            <app-progress-indefinite></app-progress-indefinite>
          </div>
          <div class="panel-state" *ngIf="noData">
            <span class="bg-text">There is no data for this time period</span>
          </div>

          <ng-container *ngIf="!loading && !noData">
          <!-- Legend: centred in the heat map, and interactive on click only —
               the same show/hide the Status Overview widget's legend does, with
               no hover behaviour. Clicking an entry switches it off (status ->
               its cells blank out, indicator -> that badge leaves every tile) and
               clicking again brings it back. Everything starts on — the extract
               legend has no INTERIM entry, so the tile outline is not something
               this screen lets you switch off. The swatch carries the state,
               so nothing is dimmed and no extra chrome appears. Colours and icons
               resolve from status-overview/config.ts. -->
          <div class="legend-row">
            <span class="legend-prefix">Status:</span>
            <div class="legend-pill">
              <div
                class="lp-item"
                *ngFor="let l of heatLegend"
                (click)="toggleStatus(l.label)"
                [attr.title]="
                  (isStatusShown(l.label) ? 'Hide ' : 'Show ') + l.label
                "
              >
                <!-- filled dot while shown, hollow ring while hidden — the two
                     states duc-legend renders for enabled / disabled -->
                <span
                  class="lp-dot"
                  [style.background]="isStatusShown(l.label) ? l.color : null"
                  [style.border-color]="l.color"
                ></span>
                <span class="lp-label">{{ l.label }}</span>
              </div>
            </div>

            <span class="legend-prefix">Indicator:</span>
            <div class="legend-pill">
              <div
                class="lp-item"
                *ngFor="let ind of indicatorLegend"
                (click)="toggleIndicator(ind.name)"
                [attr.title]="
                  (isIndicatorShown(ind.name) ? 'Hide ' : 'Show ') + ind.label
                "
              >
                <!-- the icon while shown, an empty circle while hidden — same
                     swap as the widget's static legend -->
                <span class="lp-ind">
                  <img
                    *ngIf="isIndicatorShown(ind.name)"
                    [src]="ind.url"
                    [alt]="ind.name"
                  />
                  <svg
                    *ngIf="!isIndicatorShown(ind.name)"
                    viewBox="0 0 12 12"
                    width="12"
                    height="12"
                  >
                    <circle
                      cx="6"
                      cy="6"
                      r="5.7"
                      fill="none"
                      stroke="#ddd"
                      stroke-width=".6"
                    />
                  </svg>
                </span>
                <span class="lp-label">{{ ind.label }}</span>
              </div>
            </div>
          </div>

          <!-- ===================== HISTORY: MONTH CALENDARS =================
               The History button's view. Same tree and same cells as the Live
               strip below — only the layout differs, so the tiles, the comment
               popup, the hover panel and both legends above are the shared ones
               rather than a second implementation. The double click is the one
               interaction it does not share: see the .cal-day bindings below.

               A calendar is a whole month wide, so it cannot give every tree
               node a row the way the strip does: the tree here selects which
               node the months describe. Every level is selectable, because a
               parent's cells already aggregate its branch. -->
          <div class="cal-layout" *ngIf="isHistory">
            <!-- Same fitted width the strip's tree column uses, rather than a flat
                 240px: a row here carries a label, a fold and two pill columns, and
                 at 240 the pills left the file type's name with nothing to
                 ellipsize into. -->
            <div class="cal-tree" [style.width.px]="heatTreeColPx">
              <!-- Two gestures on one row, not one click doing two things: a
                   plain click picks the node the calendars describe, which this
                   view cannot do without, and ctrl/cmd+click picks that branch
                   into the heat panel's selection — highlighting it across the KPI
                   row, the donut and the grid while deliberately leaving the
                   calendar where it is, so a filter can be built without the
                   months jumping to another node. See onCalTreeRowClick(). -->
              <div
                class="cal-tree-row xf"
                *ngFor="let row of visibleHeatRows"
                [class.selected]="isCalNode(row)"
                [class.xf-lit]="isPathPicked(row.id)"
                [class.xf-dim]="row.dimmed"
                [style.padding-left.px]="row.level * 16"
                [title]="calTreeTitle(row.label)"
                (click)="onCalTreeRowClick(row, $event)"
              >
                <button
                  class="fold"
                  *ngIf="row.hasChildren"
                  (click)="toggleHeat(row); $event.stopPropagation()"
                  [class.collapsed]="!row.expanded"
                >
                  {{ row.expanded ? "–" : "+" }}
                </button>
                <span class="fold-spacer" *ngIf="!row.hasChildren"></span>
                <span
                  class="node-label"
                  [class.lvl0]="row.level === 0"
                  (mouseenter)="onTreeLabelHover($event)"
                  >{{ row.label }}</span
                >
                <span class="row-pill" *ngIf="row.freq" [style.min-width.px]="freqColPx">{{
                  row.freq
                }}</span>
                <span
                  class="row-pill"
                  *ngIf="row.cutoff"
                  [style.min-width.px]="cutoffColPx"
                  >{{ row.cutoff }}</span
                >
              </div>
            </div>

            <!-- The note and the months share the layout's right-hand side, so
                 they need a column of their own — dropping the note straight into
                 .cal-layout would make it a third flex child beside the tree. -->
            <div class="cal-right">
            <!-- Above the last level a tile is an aggregate of the levels below
                 it, so it carries no count and answers neither a hover nor a
                 click (isCellInteractive). Said out loud, because a tile showing
                 bands and responding to nothing otherwise just reads as broken. -->
            <div
              class="cal-note"
              *ngIf="calNode && !calNodeIsLastLevel"
              [title]="calNode.label"
            >
              Showing totals for <b>{{ calNode.label }}</b> — open it in the tree
              and pick a last-level row for counts and cell details.
            </div>

            <div class="cal-months">
              <div class="cal-month" *ngFor="let m of calMonths">
                <div class="cal-month-label">{{ m.label }}</div>
                <!-- Both grids are 8 columns: a date gutter, then the seven
                     weekdays. The gutter is a real column rather than a margin
                     with the dates absolutely positioned into it, which is what
                     let them drift over the tiles. Same columns and same gap on
                     both, so a heading always sits over its own column. -->
                <div class="cal-grid cal-weekdays">
                  <div></div>
                  <div *ngFor="let w of calWeekdays">{{ w }}</div>
                </div>
                <div class="cal-grid cal-days">
                  <ng-container *ngFor="let week of m.weeks">
                    <div class="cal-dom">{{ week.label }}</div>
                    <!-- The strip's tile interactions on the day instead of the
                         <td>, minus the double click: the control chart it opens
                         is the Live strip's alone, so a month calendar carries no
                         dblclick binding at all. Two rapid clicks then settle
                         into the one comment popup a single click already opens —
                         keeping a handler here to swallow them instead would
                         clear the pending click timer and open nothing, which
                         reads as a dead tile. The handlers re-check the level
                         through isCellInteractive(), so a tile above the last
                         level stays inert here exactly as it does there. -->
                    <!-- ctrl/cmd+click selects the day — the same gesture every
                         other visual answers — which is how the History view
                         reaches what the Live strip's date headers offer: a month
                         grid has no per-day header to put them on. The plain click
                         is left exactly as it was (the comment popup): it is the
                         interaction this view already had, and the modifier adds
                         selecting without taking it away. See onCalDayClick(). -->
                    <div
                      class="cal-day"
                      *ngFor="let d of week.days"
                      [class.offset]="d.offset"
                      (click)="onCalDayClick(d, $event)"
                      (mouseenter)="onCalDayEnter(d, $event)"
                      (mouseleave)="hideCellTip()"
                    >
                      <ng-container
                        *ngTemplateOutlet="
                          heatTile;
                          context: { cell: d.cell, row: calNode }
                        "
                      ></ng-container>
                    </div>
                  </ng-container>
                </div>
              </div>

              <div class="cal-empty" *ngIf="!calMonths.length">
                Select a row to see its calendar
              </div>
            </div>
            </div>
            <!-- /.cal-right -->
          </div>

          <!-- (scroll) closes an open tile tooltip: it is fixed-positioned, so
               scrolling the strip would otherwise leave it pointing at a tile
               that has moved out from under it. -->
          <div
            class="heat-scroll"
            #heatScrollEl
            *ngIf="!isHistory"
            (scroll)="hideCellTip()"
          >
            <!-- min-width keeps every date column wide enough to print its
                 widest leaf value legibly (the strip scrolls instead of
                 squeezing digits); --heat-count-fs then adapts the font to the
                 width the column actually got. Both are single bindings on the
                 table rather than per-cell, so they cost nothing per tile. -->
            <table
              class="heat-table"
              [style.min-width.px]="heatTableMinPx"
              [style.--heat-count-fs]="heatCountFontPx + 'px'"
            >
              <thead>
                <tr>
                  <!-- The tree column is sized to the tree that is actually on
                       screen (heatTreeColPx), so expanding a branch widens it for
                       the deeper indent and collapsing hands the space back to the
                       tiles. table-layout is fixed, so this one header cell sets
                       the column for every row. -->
                  <th class="heat-tree-head" [style.width.px]="heatTreeColPx"></th>
                  <!-- A date header selects its day: ctrl/cmd+click picks that
                       column, which highlights it across every widget, and Apply
                       narrows the page to it. The header is the only part of a
                       column free to take a click at all — the tiles under it
                       already answer a single click (comments) and a double one
                       (control chart). -->
                  <th
                    class="heat-date xf"
                    *ngFor="let c of dateColumns"
                    [class.xf-lit]="isDayPicked(c.iso)"
                    [class.xf-dim]="isDayDimmed(c.iso)"
                    [title]="xfTitleFor(c.dd + ' ' + c.mon)"
                    (click)="onDateHeaderClick(c.iso, $event)"
                  >
                    <div>{{ c.dd }} {{ c.mon }}</div>
                    <div class="wd">{{ c.wd }}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of visibleHeatRows">
                  <!-- The cell keeps a flat 8px of padding and the per-level
                       indent sits on .tree-cell, so the content of each level
                       lands on the same 16px grid.

                       It is also where a PICKED row is marked, rather than on the
                       .tree-cell inside it: the mark is a rail down the left of the
                       row — the way the processing grid marks a row — and a rail
                       wants the width of the whole cell, where .tree-cell only ever
                       has the width of its label. Its own class name rather than the
                       shared .xf-lit, because it draws nothing .xf-lit draws. See
                       .heat-tree in the stylesheet. -->
                  <td
                    class="heat-tree"
                    [class.tree-row-pick]="isPathPicked(row.id)"
                  >
                    <!-- The flex wrapper is what lets the label ellipsize: a
                         table cell will not shrink its own content, so the label
                         needs a flex parent it is allowed to shrink inside
                         (.tree-cell carries min-width: 0). Without it a long name
                         runs on over the first tiles.

                         It is also the row's cross-filter target: ctrl/cmd+click
                         picks that branch, which highlights it across the KPI row,
                         the donut and the grid, and Apply commits it as a chip.
                         Nothing else claimed the modifier click in the Live strip
                         — only the fold claims a plain one, and that stops
                         propagation of its own so it keeps folding. -->
                    <div
                      class="tree-cell xf"
                      [style.margin-left.px]="row.level * 16"
                      [class.xf-dim]="row.dimmed"
                      [title]="xfTitleFor(row.label)"
                      (click)="onHeatRowClick(row, $event)"
                    >
                      <button
                        class="fold"
                        *ngIf="row.hasChildren"
                        (click)="toggleHeat(row); $event.stopPropagation()"
                        [class.collapsed]="!row.expanded"
                      >
                        {{ row.expanded ? "–" : "+" }}
                      </button>
                      <!-- A leaf holds the fold's place instead of closing the gap.
                           Without it a level-1 row's label starts 26px to the LEFT
                           of its own parent's, so the children read as if they were
                           the outer level. -->
                      <span class="fold-spacer" *ngIf="!row.hasChildren"></span>
                      <!-- title is set on hover, and only when the column had to
                           clip this label — see onTreeLabelHover(). -->
                      <span
                        class="node-label"
                        [class.lvl0]="row.level === 0"
                        (mouseenter)="onTreeLabelHover($event)"
                        >{{ row.label }}</span
                      >
                      <!-- Schedule and cutoff, on file-type rows only: on an
                           aggregate they would have to summarise every schedule
                           beneath it. They never shrink, so the label above gives
                           up its width first — updateHeatTreeWidth() budgets for
                           them via pillWidth(). -->
                      <span
                        class="row-pill"
                        *ngIf="row.freq"
                        [style.min-width.px]="freqColPx"
                        >{{ row.freq }}</span
                      >
                      <span
                        class="row-pill"
                        *ngIf="row.cutoff"
                        [style.min-width.px]="cutoffColPx"
                        >{{ row.cutoff }}</span
                      >
                    </div>
                  </td>
                  <td
                    class="heat-cell"
                    *ngFor="let cell of row.cells"
                    (click)="onCellClick(cell, row)"
                    (dblclick)="onCellDblClick(cell, row)"
                    (mouseenter)="onCellEnter(cell, row, $event)"
                    (mouseleave)="hideCellTip()"
                  >
                    <ng-container
                      *ngTemplateOutlet="
                        heatTile;
                        context: { cell: cell, row: row }
                      "
                    ></ng-container>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          </ng-container>
        </div>

        <!-- One tile, rendered by both views. The Live strip puts it in a table
             cell and the History calendar in a day of the month grid, but a tile
             means the same thing in both, so it is defined once — the bands, the
             count, the anomaly outline and the indicator badges cannot drift
             apart between the two.

             Presentation only: each view attaches its own click / hover handlers
             to whatever contains the tile, because the strip's live on the <td>
             and the calendar's on the day. `cell` is null for a calendar day the
             range has no column for, and `row` is null before a node has been
             selected — both render the empty slot. -->
        <ng-template #heatTile let-cell="cell" let-row="row">
          <!-- The tile is one band per status present in the cell, so a parent
               row shows every status in its branch for that day rather than only
               the most severe. A band is the unit throughout: it carries its own
               count and its own indicator badges, so everything drawn on the tile
               except the anomaly outline belongs to one named status. A cell with
               nothing in it — no records, or every status switched off in the
               legend — renders as the single empty slot: no bands, no count, no
               badges, not clickable.
               No `title`: hovering opens the rich .cell-tip panel below, and a
               native tooltip would surface on top of it.
               .clickable spells out isCellInteractive() rather than calling it,
               so the cursor states cost nothing per tile — keep the two in
               step. -->
          <ng-container *ngIf="cell; else emptyTile">
                    <!-- .xf-dim is the cross-highlight: the tile holds records
                         and none of them are what is highlighted, so it fades
                         back rather than being filtered away — the Power BI
                         reading of a click, where the part is shown against the
                         whole. Stamped onto the cell by applyHighlight() rather
                         than tested here, because a long range is thousands of
                         tiles and this binding runs on every one of them. -->
                    <div
                      class="bar"
                      [class.empty]="!cell.shownSegments.length"
                      [class.xf-dim]="cell.dim"
                      [class.clickable]="
                        row && !row.hasChildren && !cell.hidden && cell.rows.length
                      "
                    >
                      <!-- INTERIM marks the tile with an outline instead of a
                           corner badge, matching the Status Overview tile
                           (dem/config.ts's borderBadgeAccessor -> .borderInd). It
                           wraps the whole tile, not one band: an interim extract
                           is a second delivery of the day's file, a property of
                           the cell's records as a whole. -->
                      <span
                        class="cell-interim"
                        *ngIf="!cell.hidden && cell.showInterim"
                      ></span>

                      <!-- An outlined status (Expected) has no fill, so its band
                           carries .empty instead — the background binding has to
                           go to null there, or the inline style would outrank
                           the class. Last-level bands print their exact count;
                           anything above stays clean in both views, because its
                           value is an aggregate of the levels underneath rather
                           than a number belonging to the tile it is drawn on. -->
                      <!-- Bands dim individually as well as the tile as a whole:
                           a tile holding a Sent file and an In Process one, with
                           In Process highlighted, keeps the tile lit and fades
                           only the Sent band — which is the answer the click was
                           asking for. -->
                      <span
                        class="seg"
                        *ngFor="let seg of cell.shownSegments"
                        [class.empty]="!seg.color"
                        [class.xf-dim]="seg.dim"
                        [style.background]="seg.color || null"
                      >
                        <span
                          class="cell-count"
                          [class.on-pale]="seg.onPale"
                          *ngIf="row && !row.hasChildren && seg.countLabel"
                          >{{ seg.countLabel }}</span
                        >

                        <!-- Badges belong to the BAND, not the tile: an indicator
                             is a property of a file status, so a DEFECT icon sits
                             over the Failed band rather than floating above a tile
                             that also holds Sent files. Both views draw them
                             here, because both render this one template.

                             Every badge the band carries, as long as it has room
                             for them side by side — badgesCollapsed() works that
                             out from the band's share of the measured tile rather
                             than capping the count, so a band shows its whole set
                             whenever it can. Only when it cannot does the set give
                             way to a single ⋯ disc, which unstacks into the icons
                             on hover.

                             No `title` on any of them — a badge sits on top of the
                             tile, so its native tooltip would fight the cell
                             panel. -->
                        <span
                          class="cell-badges"
                          *ngIf="seg.shownIndicators.length"
                        >
                          <ng-container
                            *ngIf="!badgesCollapsed(seg, cell); else moreBadges"
                          >
                            <img
                              *ngFor="let ind of seg.shownIndicators"
                              class="cell-badge"
                              [src]="ind.url"
                              [alt]="ind.name"
                            />
                          </ng-container>
                          <!-- pointer-events back on (.cell-badges switches them
                               off so clicks reach the tile), because this disc is
                               the one badge that answers a hover. -->
                          <ng-template #moreBadges>
                            <span
                              class="cell-badge cell-badge-more"
                              (mouseenter)="showBadgePop(seg, $event)"
                              (mouseleave)="hideBadgePop()"
                            >
                              <!-- 10px wide: the content box of the 12px disc,
                                   whose padding is 1px. -->
                              <svg viewBox="0 0 16 4" width="10" height="4">
                                <circle cx="2" cy="2" r="1.6" fill="#1363df" />
                                <circle cx="8" cy="2" r="1.6" fill="#1363df" />
                                <circle cx="14" cy="2" r="1.6" fill="#1363df" />
                              </svg>
                            </span>
                          </ng-template>
                        </span>
                      </span>
                    </div>
          </ng-container>
          <!-- No cell at all: the same outlined slot an all-bands-hidden tile
               falls back to, so a day outside the data reads as empty rather
               than as a gap in the grid. -->
          <ng-template #emptyTile>
            <div class="bar empty"></div>
          </ng-template>
        </ng-template>

        <!-- Tile hover tooltip, opened on the last (leaf) level only — same as
             the reference widget, where an aggregate row has no `details` for
             the panel to render. The panel itself is the reused Status Overview
             cell component, handed the very payload the click popups get, so the
             tooltip can never describe a cell differently from the comment modal
             or the control chart.

             It sits inside #heatFsEl (so it still renders while the panel is
             fullscreen — nothing outside the promoted element paints) but
             outside #heatExportEl (so it can never turn up in "Export as
             Image"), and it is position: fixed, so .heat-scroll's overflow
             cannot clip it.

             [style.border-radius] is the caret corner: placeCellTip() squares
             off whichever corner landed on the tile, the way duc-tooltip does. -->
        <div
          class="cell-tip"
          #cellTipEl
          *ngIf="tipData"
          [style.left.px]="tipPos.left"
          [style.top.px]="tipPos.top"
          [style.border-radius]="tipRadius"
        >
          <app-status-cell-info-dem
            [data]="tipData"
            [header]="true"
          ></app-status-cell-info-dem>
        </div>

        <!-- The ⋯ disc unstacked: the icons of the band it sits on, rising out of
             it in the same discs they wear on a tile. Icons alone — the indicator
             legend above the heat map is what names them.
             Sits beside .cell-tip rather than inside the tile for the same reasons
             — inside #heatExportEl it would turn up in "Export as Image", and
             position: fixed keeps it clear of the strip's overflow. Above the
             tooltip, because reaching the disc means both are open. -->
        <div
          class="badge-pop"
          *ngIf="badgePop"
          [style.left.px]="badgePop.left"
          [style.top.px]="badgePop.top"
        >
          <img
            class="badge-pop-icon"
            *ngFor="let ind of badgePop.indicators"
            [src]="ind.url"
            [alt]="ind.name"
          />
        </div>
      </article>
    </div>

    <!-- ======================== DATA PROCESSING STATUS ===================
         Hosts the same shared <icon-chart> header as the heat map above, so
         Fullscreen / Expand All / the export menu are the same components with
         the same behaviour and the same icons. #gridFsEl is what icon-chart
         puts into fullscreen; #gridExportEl is the subtree it rasterises for
         "Export as Image". -->
    <div class="panel grid-panel" data-xf-widget="grid" [class.xf-selecting]="isSelecting('grid')">
      <article
        #gridFsEl
        fullscreen-able
        class="grid-fs"
        [class.is-active]="gridFullscreen"
      >
        <icon-chart
          [headerConfig]="gridHeaderConfig"
          [heading]="gridTitle"
          [chartName]="'DemStaticExtractGrid'"
          [item]="gridItem"
          [staticInfo]="gridHelp"
          [data]="gridExcelRows"
          [excelConfig]="gridExcelConfig"
          [config]="gridExportEl"
          [expand]="gridFsEl"
          [fullscreen]="gridFullscreen"
          (screenChanges)="onGridFullscreen()"
          (expendCol)="onGridExpandToggle($event)"
        ></icon-chart>

        <div #gridExportEl class="grid-export">
          <!-- Same two states as the heat panel above, off the same flags and
               inside the export ref for the same reason — the two panels render
               the one dataset, so they are never in disagreement about whether
               there is anything to show. -->
          <div class="panel-state" *ngIf="loading">
            <app-progress-indefinite></app-progress-indefinite>
          </div>
          <div class="panel-state" *ngIf="noData">
            <span class="bg-text">There is no data for this time period</span>
          </div>

          <!-- The grid carries the same hierarchy as the heat map above (the
               rows are mapped straight off its nodes), so it is fed the
               flattened, expansion-aware row list: one ag-Grid row per entry,
               indented by level, with the +/- fold drawn by the tree column's
               renderer. Column-per-level would only ever fit a fixed depth.

               autoHeight + no virtualisation on purpose: the panel grows with
               its rows the way the table did, and icon-chart's "Export as
               Image" rasterises this subtree — a virtualised grid would hand it
               only the rows that happen to be in view.

               sortChanged is what actually sorts, since ag-Grid's own sort is
               deliberately inert here — see gridDefaultColDef. -->
          <div class="grid-scroll" *ngIf="!loading && !noData">
            <ag-grid-angular
              class="ag-theme-balham dpm-ag-grid"
              [columnDefs]="gridColumnDefs"
              [defaultColDef]="gridDefaultColDef"
              [rowData]="visibleGridRows"
              [domLayout]="'autoHeight'"
              [headerHeight]="38"
              [rowHeight]="36"
              [animateRows]="false"
              [suppressCellFocus]="true"
              [suppressRowVirtualisation]="true"
              [suppressColumnVirtualisation]="true"
              [suppressContextMenu]="true"
              [rowClassRules]="gridRowClassRules"
              (cellClicked)="onGridCellClicked($event)"
              (sortChanged)="onGridSortChanged($event)"
            ></ag-grid-angular>
          </div>
        </div>
      </article>
    </div>
    </div>
    <!-- /.dem-scroll -->
  </div>
  <!-- /.dem-body -->

  <!-- ===================== RIGHT FILTER PANEL (like app-filter) ========== -->
  <div
    class="filter-panel-backdrop"
    *ngIf="filterPanelOpen"
    (click)="closeFilterPanel()"
  ></div>

  <aside
    class="filter-panel"
    [class.open]="filterPanelOpen"
    #fpEl
    [style.width.px]="panelWidth"
  >
    <!-- drag the left edge to resize (same as app-filter) -->
    <div
      class="fp-resize"
      (resize)="onResizeSide($event)"
      [targetElement]="fpEl"
      [direction]="AngularResizeElementDirection.LEFT"
      title="Drag to resize"
    ></div>

    <!-- single handle: filter icon when closed (sits at screen's right edge),
         close ✕ when open -->
    <div
      class="fp-toggle"
      (click)="toggleFilterPanel()"
      [title]="filterPanelOpen ? 'Close' : 'Filter'"
    >
      <mat-icon *ngIf="!filterPanelOpen" svgIcon="g_filter"></mat-icon>
      <span *ngIf="filterPanelOpen" class="fp-toggle-x">✕</span>
    </div>

    <h2 class="fp-title">Filter</h2>
    <hr class="fp-hr" />

    <div class="fp-body">
      <!-- Same list as the badge strip, and gated the same way: a filter appears
           here once it has values and not before. The field used to render
           regardless, with its label standing over a readonly select that read
           "Loading…" or "No values" — a control offering nothing, which is worse
           in the panel than in the strip because the panel is a column of them
           and every empty row pushes a usable one further down.

           ng-container carries the *ngFor so the field can carry the *ngIf. -->
      <ng-container *ngFor="let f of filters">
      <div class="fp-field" *ngIf="hasOptions(f)">
        <label class="filterLabelStyle">{{ f.label }}</label>

        <!-- SINGLE SELECT (e.g. Brand) — direct selection, like the badge
             dropdown above it: picking a value applies it, so the list closes on
             the pick and there is no footer of buttons under it. closeOnSelect
             stays false on the MULTI select below, where the picks accumulate
             and Include is what commits them. -->
        <ng-select
          *ngIf="f.single"
          #scS
          class="ngSelection fp-select"
          [multiple]="false"
          [clearable]="false"
          [closeOnSelect]="true"
          [dropdownPosition]="'auto'"
          [appendTo]="'.filter-panel'"
          [items]="f.options"
          [ngModel]="f.checked[0] || null"
          (ngModelChange)="onSingleModel(f, $event)"
          (close)="onSelectClose(f)"
          [placeholder]="filterPlaceholder(f)"
        >
          <ng-template ng-header-tmp>
            <input
              class="searchFiler"
              type="text"
              placeholder="Search"
              (input)="scS.filter(onFilterText(f, $event))"
            />
          </ng-template>
          <ng-template ng-option-tmp let-item="item" let-item$="item$" let-index="index">
            <div class="form-check du-opt">
              <input
                style="accent-color: #1363df !important"
                id="opt-s-{{ index }}"
                type="checkbox"
                [checked]="item$.selected"
              />
              <label class="form-check-label">{{ item }}</label>
            </div>
          </ng-template>
        </ng-select>

        <!-- MULTI SELECT -->
        <ng-select
          *ngIf="!f.single"
          #scM
          class="ngSelection fp-select"
          [multiple]="true"
          [clearable]="false"
          [closeOnSelect]="false"
          [dropdownPosition]="'auto'"
          [appendTo]="'.filter-panel'"
          [items]="f.options"
          [ngModel]="f.checked"
          (ngModelChange)="f.checked = $event"
          (close)="onSelectClose(f)"
          [placeholder]="filterPlaceholder(f)"
        >
          <ng-template ng-header-tmp>
            <input
              class="searchFiler"
              type="text"
              placeholder="Search"
              (input)="scM.filter(onFilterText(f, $event))"
            />
            <!-- Select All and Add current selection, in the header the reference
                 multi-select puts them in and with the same meanings: Select All
                 covers the search results while a search is running, and Add current
                 selection keeps what the chip already filters by alongside the ticks
                 made here. -->
            <div class="form-check multiselect-form-check">
              <input
                style="accent-color: #1363df !important"
                type="checkbox"
                [checked]="allChecked(f)"
                (click)="toggleSelectAll(f, $event)"
              />
              <label class="form-check-label">
                <span *ngIf="!f.search">Select All</span>
                <span *ngIf="f.search">(Select All Search Results)</span>
              </label>
            </div>
            <div class="form-check multiselect-form-check">
              <input
                style="accent-color: #1363df !important"
                type="checkbox"
                [checked]="f.addCurrent"
                (click)="toggleAddCurrent(f, $event)"
              />
              <label class="form-check-label">Add current selection to filter</label>
            </div>
          </ng-template>

          <ng-template ng-multi-label-tmp let-items="items">
            <div
              class="ng-value"
              *ngFor="let it of items | slice: 0 : itemsShowLimit"
            >
              <span class="ng-value-label">{{ it }}</span>
            </div>
            <div class="ng-value" *ngIf="items.length > itemsShowLimit">
              <span class="ng-value-label"
                >{{ items.length - itemsShowLimit }} more...</span
              >
            </div>
          </ng-template>

          <ng-template ng-option-tmp let-item="item" let-item$="item$" let-index="index">
            <div class="form-check du-opt">
              <input
                style="accent-color: #1363df !important"
                id="opt-m-{{ index }}"
                type="checkbox"
                [checked]="item$.selected"
              />
              <label class="form-check-label">{{ item }}</label>
            </div>
          </ng-template>

          <ng-template ng-footer-tmp>
            <div class="d-flex align-items-center py-2" style="gap: 10px">
              <ng-container
                *ngTemplateOutlet="ieButtons; context: { f: f, sc: scM }"
              ></ng-container>
            </div>
          </ng-template>
        </ng-select>
      </div>
      </ng-container>

      <!-- Time cycle selector (hidden when only one cycle is configured) -->
      <div class="fp-field" *ngIf="showCycleSelector">
        <label class="fp-flabel">Time Cycle</label>
        <div class="time-cycle">
          <div
            class="tc-item"
            *ngFor="let c of cycleList"
            [class.active]="selectedCycle === c.value"
            [matTooltip]="c.label"
            matTooltipPosition="above"
            (click)="changeCycle(c.value)"
          >
            {{ c.value }}
          </div>
        </div>
      </div>

      <!-- Date range — picker depends on the selected cycle -->
      <ng-container [ngSwitch]="selectedCycle">
        <!-- Quarterly: from/to quarter dropdowns -->
        <ng-container *ngSwitchCase="'Q'">
          <div class="fp-field">
            <label class="fp-flabel">From</label>
            <ng-select
              class="fp-qselect"
              [items]="fromOption"
              bindLabel="label"
              [clearable]="false"
              [searchable]="false"
              [dropdownPosition]="'auto'"
              [appendTo]="'.filter-panel'"
              [(ngModel)]="selectedFromOption"
              (change)="applyQuarter()"
            ></ng-select>
          </div>

          <hr class="fp-hr" />

          <div class="fp-field">
            <label class="fp-flabel">To</label>
            <ng-select
              class="fp-qselect"
              [items]="toOption"
              bindLabel="label"
              [clearable]="false"
              [searchable]="false"
              [dropdownPosition]="'auto'"
              [appendTo]="'.filter-panel'"
              [(ngModel)]="selectedToOption"
              (change)="applyQuarter()"
            ></ng-select>
          </div>
        </ng-container>

        <!-- Daily / Weekly (day pickers) + Monthly / Yearly (month/year pickers) -->
        <ng-container *ngSwitchDefault>
          <div class="fp-field">
            <label class="fp-flabel">From Date</label>
            <mat-form-field class="fp-datepick" appearance="fill">
              <input
                matInput
                readonly
                [min]="minFromDate"
                [max]="maxFromDate"
                [ngModel]="fromDateObj"
                [matDatepicker]="fromPicker"
                (dateChange)="onFromPicked($event.value)"
              />
              <mat-datepicker-toggle
                matSuffix
                [for]="fromPicker"
              ></mat-datepicker-toggle>
              <mat-datepicker
                #fromPicker
                panelClass="dpm-cal"
                [startView]="
                  selectedCycle === 'M'
                    ? 'year'
                    : selectedCycle === 'Y'
                    ? 'multi-year'
                    : 'month'
                "
                [calendarHeaderComponent]="exampleHeader"
                (monthSelected)="onMonthFrom($event, fromPicker)"
                (yearSelected)="onMonthFrom($event, fromPicker)"
              ></mat-datepicker>
            </mat-form-field>
          </div>

          <hr class="fp-hr" />

          <div class="fp-field">
            <label class="fp-flabel">To Date</label>
            <mat-form-field class="fp-datepick" appearance="fill">
              <input
                matInput
                readonly
                [min]="minToDate"
                [max]="maxToDate"
                [ngModel]="toDateObj"
                [matDatepicker]="toPicker"
                (dateChange)="onToPicked($event.value)"
              />
              <mat-datepicker-toggle
                matSuffix
                [for]="toPicker"
              ></mat-datepicker-toggle>
              <mat-datepicker
                #toPicker
                panelClass="dpm-cal"
                [startView]="
                  selectedCycle === 'M'
                    ? 'year'
                    : selectedCycle === 'Y'
                    ? 'multi-year'
                    : 'month'
                "
                [calendarHeaderComponent]="exampleHeader"
                (monthSelected)="onMonthTo($event, toPicker)"
                (yearSelected)="onMonthTo($event, toPicker)"
              ></mat-datepicker>
            </mat-form-field>
          </div>
        </ng-container>
      </ng-container>

      <hr class="fp-hr" />
      
      <!-- Reset (shown when any filter is applied) - like app-filter .clrFilter -->
      <div class="fp-reset-row" *ngIf="anyPartial">
        <span class="fp-reset-chip" (click)="resetFilters()">
          <mat-icon class="reset-icon" svgIcon="x_mark"></mat-icon>Reset
        </span>
      </div>
    </div>
  </aside>

  <!-- ======================= CARD HELP POPOVER ==========================
       The Help popover every card in the KPI row shares — the KPI cards, the
       Extracts card and the donut. Declared once here, OUTSIDE the *ngFor that
       renders the cards: inside it, this would be one mat-menu per card. The
       trigger anchors it to whichever icon was clicked, and showCardHelp() puts
       that card's entry in `cardHelp` on the same click.

       Markup copied from the real KPI cards' own info menu (card1kpi, and the
       identical one in card2kpi and icon-chart) rather than restyled: the same
       .card / .card-header / .card-body, the same inline min-width and #ddd
       header, the same ✕ glyph. Those are global bootstrap classes and a
       mat-menu renders into an overlay outside this component, so this picks up
       exactly the styling the dynamic widgets' popovers get and needs no CSS of
       its own here. Help opened from a static card is then indistinguishable
       from help opened from a dynamic one — which also means it matches the two
       panels below, since those answer through icon-chart's copy of it.

       The text is authored HTML from assets/help_info/dem-static.json, hence
       [innerHtml]; Angular's sanitizer still runs over it. -->
  <mat-menu #cardHelpMenu="matMenu" class="kpi-info-menu">
    <div
      class="card"
      style="min-width: 300px; border: none; width: auto"
      (click)="$event.stopPropagation()"
    >
      <div
        class="card-header d-flex justify-content-between align-items-center"
        style="border-bottom: none; background: #ddd"
      >
        <span style="font-weight: 600">{{ cardHelp?.info_header }}</span>

        <!-- Closes through the menu's own `closed` output, which is what its
             trigger listens on — the same effect as the reference's closeMenu()
             without this screen needing a ViewChildren of every trigger. -->
        <span (click)="cardHelpMenu.closed.emit()">
          <svg
            width="16"
            height="16"
            style="width: 12px !important; height: 12px !important"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.9501 0.409871L8 5.35998L3.04989 0.409871C2.5034 -0.136624 1.61634 -0.136624 1.06985 0.409871L0.409871 1.06985C-0.136624 1.61634 -0.136624 2.5034 0.409871 3.04989L5.35998 8L0.409871 12.9501C-0.136624 13.4966 -0.136624 14.3837 0.409871 14.9302L1.06985 15.5901C1.61634 16.1366 2.5034 16.1366 3.04989 15.5901L8 10.64L12.9501 15.5901C13.4966 16.1366 14.3837 16.1366 14.9302 15.5901L15.5901 14.9302C16.1366 14.3837 16.1366 13.4966 15.5901 12.9501L10.64 8L15.5901 3.04989C16.1366 2.5034 16.1366 1.61634 15.5901 1.06985L14.9302 0.409871C14.3837 -0.136624 13.4966 -0.136624 12.9501 0.409871Z"
              fill="#1363DF"
            />
          </svg>
        </span>
      </div>

      <div class="card-body">
        <div [innerHtml]="cardHelp?.info_text || ''"></div>
      </div>
    </div>
  </mat-menu>
</div>
